if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mediaOrder = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'), item => item.dataset.mediaId);
      this.counter = this.querySelector('[data-gallery-current]');
      this.mql = window.matchMedia('(min-width: 750px)');
      if (this.hasAttribute('data-canvoil-carousel') && this.elements.thumbnails) {
        this.initCarousel();
        return;
      }
      if (this.closest('.product-section')) {
        window.addEventListener('pageshow', () => {
          if (this.dataset.initialMedia && this.elements.thumbnails) {
            this.setActiveMedia(this.dataset.initialMedia, true);
            this.elements.thumbnails.querySelector('.thumbnail-list')?.scrollTo({ left: 0, behavior: 'instant' });
          }
          if (!window.location.hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        });
      }
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.viewer.slider?.addEventListener('scroll', debounce(() => {
        if (this.mql.matches) return;
        const slider = this.elements.viewer.slider;
        const slides = Array.from(slider.querySelectorAll(':scope > [data-media-id]'));
        const active = slides.reduce((nearest, slide) => !nearest || Math.abs(slide.offsetLeft - slider.scrollLeft) < Math.abs(nearest.offsetLeft - slider.scrollLeft) ? slide : nearest, null);
        if (active) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${active.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
      }, 120));

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        button.type = 'button';
        const prepare = () => {
          if (!this.mql.matches || this.dataset.desktopLayout === 'stacked') return;
          const media = this.elements.viewer.querySelector(`[data-media-id="${mediaToSwitch.dataset.target}"]`);
          const image = media?.querySelector('.product__media img');
          if (image) this.prepareImage(image);
        };
        button.addEventListener('pointerenter', prepare);
        button.addEventListener('focus', prepare);
        button.addEventListener('pointerdown', prepare);
        button.addEventListener('click', () => this.setActiveMedia(mediaToSwitch.dataset.target, false));
      });
      this.bindDesktopGestures();
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    // The product-page carousel owns one selected media ID. Third-party variant
    // initializers may suggest a media item, but may not reset an active visit.
    initCarousel() {
      this.carousel = this.elements.viewer.querySelector('.product__media-list');
      if (!this.carousel) return;
      this.carouselEvents = new AbortController();
      const signal = this.carouselEvents.signal;
      this.currentMediaId = this.carousel.querySelector('.is-active')?.dataset.mediaId;
      this.mediaOrder = Array.from(this.carousel.children, item => item.dataset.mediaId);
      this.feedback = this.querySelector('[data-gallery-feedback]');
      this.carousel.classList.add('canvoil-carousel-ready');
      this.carousel.addEventListener('scroll', () => {
        clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = setTimeout(() => this.settleCarousel(), 140);
      }, { passive: true, signal });
      this.carousel.addEventListener('scrollend', () => this.settleCarousel(), { signal });
      this.carousel.addEventListener('pointerdown', () => this.recordGalleryIntent(), { passive: true, signal });
      this.carousel.addEventListener('wheel', (event) => {
        if (event.deltaX || event.shiftKey) this.recordGalleryIntent();
      }, { passive: true, signal });
      this.elements.thumbnails.querySelectorAll('[data-target] button').forEach((button) => {
        const mediaId = button.closest('[data-target]').dataset.target;
        button.type = 'button';
        button.addEventListener('click', () => this.selectCarouselMedia(mediaId, { source: 'user' }), { signal });
        const prepare = () => {
          const image = this.carouselItems().find(item => item.dataset.mediaId === mediaId)?.querySelector('.product__media img');
          if (image) this.prepareImage(image);
        };
        button.addEventListener('pointerenter', prepare, { signal });
        button.addEventListener('focus', prepare, { signal });
      });
      this.querySelectorAll('[data-gallery-step]').forEach(button => {
        button.addEventListener('click', () => this.navigateCarousel(Number(button.dataset.galleryStep)), { signal });
      });
      this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
        this.carouselItems().find(item => item.dataset.mediaId === this.currentMediaId)?.querySelector('.product__media:not(.hidden) > a')?.click();
      }, { signal });
      this.feedback?.addEventListener('click', () => {
        const item = this.carouselItems().find(item => item.dataset.mediaId === this.currentMediaId);
        const image = item?.querySelector('.product__media img');
        if (image && !image.naturalWidth) image.src = image.currentSrc || image.src;
        if (item) this.watchCarouselImage(item);
      }, { signal });
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.target.closest('input, select, textarea, deferred-media, product-model')) return;
        event.preventDefault();
        const items = this.carouselItems();
        if (event.key === 'Home' || event.key === 'End') {
          const item = event.key === 'Home' ? items[0] : items[items.length - 1];
          if (item) this.selectCarouselMedia(item.dataset.mediaId, { source: 'user' });
        } else this.navigateCarousel(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      // Only an actual customer choice authorizes a later variant-image update.
      // App-generated initialization/change events must not steal the gallery.
      const section = this.closest('.product-section');
      this.variantScope = section;
      const variantIntent = event => {
        if (!event.isTrusted) return;
        if (event.type === 'keydown' && !['Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key) && !(event.target.tagName === 'SELECT' && event.key.length === 1)) return;
        if (event.target.closest('variant-radios, variant-selects, .globo-swatch-product-detail')) {
          this.variantSelectionBefore = this.variantSelectionKey();
          this.variantInteractionUntil = Date.now() + 5000;
        }
      };
      section?.addEventListener('pointerdown', variantIntent, { capture: true, signal });
      section?.addEventListener('keydown', variantIntent, { capture: true, signal });
      this.bindCarouselDrag(signal);
      this.carouselWidth = this.carousel.clientWidth;
      this.carouselResize = new ResizeObserver(() => {
        const width = this.carousel.clientWidth;
        if (!width || Math.abs(width - this.carouselWidth) < 1) return;
        this.carouselWidth = width;
        this.targetMediaId = null;
        this.alignCarousel(this.currentMediaId, true);
      });
      this.carouselResize.observe(this.carousel);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.alignCarousel(this.currentMediaId, true);
      }, { signal });
      this.updateCarouselState(this.currentMediaId);
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        if (!this.userNavigated) this.alignCarousel(this.currentMediaId, true);
        this.prepareCarouselNeighbors();
      });
    }

    connectedCallback() {
      if (this.carouselEvents?.signal.aborted) this.initCarousel();
    }

    disconnectedCallback() {
      const drag = this.carouselDrag;
      this.carouselDrag = null;
      this.targetMediaId = null;
      this.carousel?.classList.remove('canvoil-gallery-dragging');
      if (drag && this.carousel?.hasPointerCapture(drag.id)) this.carousel.releasePointerCapture(drag.id);
      this.carouselEvents?.abort();
      this.carouselResize?.disconnect();
      clearTimeout(this.scrollSettleTimer);
      this.carouselImageRequest = (this.carouselImageRequest || 0) + 1;
    }

    carouselItems() {
      return Array.from(this.carousel.children).filter(item => item.matches('[data-media-id]') && !item.hidden && getComputedStyle(item).display !== 'none');
    }

    recordGalleryIntent() {
      this.userNavigated = true;
      this.variantInteractionUntil = 0;
      this.targetMediaId = null;
    }

    variantSelectionKey() {
      const fields = this.variantScope?.querySelector('.globo-swatch-product-detail') || this.variantScope?.querySelector('variant-radios, variant-selects');
      return Array.from(fields?.querySelectorAll('input:checked, select') || [], input => `${input.name}:${input.value}`).join('|');
    }

    navigateCarousel(direction) {
      const items = this.carouselItems();
      const current = Math.max(0, items.findIndex(item => item.dataset.mediaId === (this.targetMediaId || this.currentMediaId)));
      const next = Math.max(0, Math.min(items.length - 1, current + direction));
      if (items[next]) this.selectCarouselMedia(items[next].dataset.mediaId, { source: 'user' });
    }

    selectCarouselMedia(mediaId, options = {}) {
      const items = this.carouselItems();
      const item = items.find(item => item.dataset.mediaId === mediaId);
      if (!item) return;
      const variantChanged = Date.now() < (this.variantInteractionUntil || 0) && this.variantSelectionKey() !== this.variantSelectionBefore;
      if (options.external && this.userNavigated && !variantChanged && items.some(item => item.dataset.mediaId === this.currentMediaId)) return;
      if (options.external) this.variantInteractionUntil = 0;
      if (options.source === 'user' || options.source === 'lightbox') this.recordGalleryIntent();
      if (options.external && mediaId === this.currentMediaId) return;
      this.targetMediaId = mediaId;
      this.updateCarouselState(mediaId);
      this.watchCarouselImage(item);
      this.alignCarousel(mediaId, options.instant);
      this.prepareCarouselNeighbors();
    }

    alignCarousel(mediaId, instant = false) {
      const item = this.carouselItems().find(item => item.dataset.mediaId === mediaId);
      if (!item) return;
      const left = item.offsetLeft - this.carouselItems()[0].offsetLeft;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.carousel.scrollTo({ left, behavior: instant || reduced ? 'instant' : 'smooth' });
      clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = setTimeout(() => this.settleCarousel(), instant || reduced ? 0 : 160);
    }

    settleCarousel() {
      if (this.carouselDrag || !this.carousel) return;
      const items = this.carouselItems();
      if (!items.length) return;
      const start = items[0].offsetLeft;
      const nearest = items.reduce((best, item) => Math.abs(item.offsetLeft - start - this.carousel.scrollLeft) < Math.abs(best.offsetLeft - start - this.carousel.scrollLeft) ? item : best);
      // Interrupted smooth scrolls can emit an old scrollend after a newer
      // thumbnail/arrow request. Only settle that request at its destination.
      if (this.targetMediaId && nearest.dataset.mediaId !== this.targetMediaId && items.some(item => item.dataset.mediaId === this.targetMediaId)) return;
      this.targetMediaId = null;
      if (nearest.dataset.mediaId !== this.currentMediaId) {
        this.updateCarouselState(nearest.dataset.mediaId);
        this.watchCarouselImage(nearest);
      }
      this.prepareCarouselNeighbors();
    }

    updateCarouselState(mediaId) {
      const items = this.carouselItems();
      const selected = items.find(item => item.dataset.mediaId === mediaId);
      if (!selected) return;
      this.currentMediaId = mediaId;
      Array.from(this.carousel.children).forEach(item => {
        const active = item === selected;
        item.classList.toggle('is-active', active);
        item.inert = !active;
        item.setAttribute('aria-hidden', String(!active));
      });
      const index = items.indexOf(selected);
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
      this.querySelectorAll('[data-gallery-step], [data-gallery-navigation] > button').forEach(button => {
        const previous = button.dataset.galleryStep === '-1' || button.name === 'previous';
        const disabled = previous ? index === 0 : index === items.length - 1;
        button.setAttribute('aria-disabled', String(disabled));
        if (button.hasAttribute('data-gallery-step')) button.disabled = disabled;
      });
      const zoom = this.querySelector('[data-gallery-zoom]');
      const link = selected.querySelector('.product__media:not(.hidden) > a');
      if (zoom) {
        zoom.hidden = !link;
        const label = selected.querySelector('.product__media-toggle')?.textContent.trim();
        if (label) { zoom.setAttribute('aria-label', label); link?.setAttribute('aria-label', label); }
      }
      if (this.elements.liveRegion) this.elements.liveRegion.textContent = `${index + 1} / ${items.length}`;
      this.playActiveMedia(selected);
    }

    watchCarouselImage(item) {
      const image = item.querySelector('.product__media:not(.hidden) img');
      const request = this.carouselImageRequest = (this.carouselImageRequest || 0) + 1;
      if (this.feedback) this.feedback.hidden = true;
      this.elements.viewer.removeAttribute('aria-busy');
      if (!image) return;
      image.fetchPriority = 'auto';
      if (!image.complete || !image.naturalWidth) this.elements.viewer.setAttribute('aria-busy', 'true');
      this.prepareImage(image).then(ready => {
        if (request !== this.carouselImageRequest || !this.isConnected) return;
        this.elements.viewer.removeAttribute('aria-busy');
        if (!ready && this.feedback) this.feedback.hidden = false;
      });
    }

    prepareCarouselNeighbors() {
      if (navigator.connection?.saveData) return;
      const items = this.carouselItems();
      const index = items.findIndex(item => item.dataset.mediaId === this.currentMediaId);
      [items[index - 1], items[index + 1]].forEach(item => {
        const image = item?.querySelector('.product__media:not(.hidden) img');
        if (image) { image.fetchPriority = 'low'; this.prepareImage(image); }
      });
    }

    bindCarouselDrag(signal) {
      const surface = this.carousel;
      surface.addEventListener('dragstart', event => event.preventDefault(), { signal });
      surface.addEventListener('pointerdown', event => {
        // During a smooth transition, inactive slides are inert and pointer
        // events retarget to the track. That surface must remain draggable.
        if (!this.mql.matches || event.pointerType !== 'mouse' || event.button !== 0 || (event.target !== surface && !event.target.closest('.product__media'))) return;
        this.targetMediaId = null;
        surface.scrollTo({ left: surface.scrollLeft, behavior: 'instant' });
        this.carouselDrag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: surface.scrollLeft, moved: false };
        this.prepareCarouselNeighbors();
      }, { signal });
      surface.addEventListener('pointermove', event => {
        const drag = this.carouselDrag;
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.carouselDrag = null; return; }
          if (Math.abs(dx) < 8) return;
          drag.moved = true;
          surface.classList.add('canvoil-gallery-dragging');
          surface.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        surface.scrollLeft = drag.left - dx;
      }, { signal });
      const finish = (event, cancelled = false) => {
        const drag = this.carouselDrag;
        if (!drag || drag.id !== event.pointerId) return;
        this.carouselDrag = null;
        surface.classList.remove('canvoil-gallery-dragging');
        if (surface.hasPointerCapture(drag.id)) surface.releasePointerCapture(drag.id);
        if (!drag.moved) return;
        this.suppressCarouselClickUntil = Date.now() + 500;
        const items = this.carouselItems();
        const start = items[0]?.offsetLeft || 0;
        const origin = items.reduce((best, item) => Math.abs(item.offsetLeft - start - drag.left) < Math.abs(best.offsetLeft - start - drag.left) ? item : best, items[0]);
        const dx = event.clientX - drag.x;
        const direction = cancelled || Math.abs(dx) < Math.min(100, surface.clientWidth * 0.15) ? 0 : dx < 0 ? 1 : -1;
        const index = Math.max(0, Math.min(items.length - 1, items.indexOf(origin) + direction));
        if (items[index]) this.selectCarouselMedia(items[index].dataset.mediaId, { source: 'user' });
      };
      surface.addEventListener('pointerup', event => finish(event), { signal });
      surface.addEventListener('pointercancel', event => finish(event, true), { signal });
      surface.addEventListener('lostpointercapture', event => finish(event, true), { signal });
      surface.addEventListener('pointerleave', () => { if (this.carouselDrag && !this.carouselDrag.moved) this.carouselDrag = null; }, { signal });
      surface.addEventListener('click', event => {
        if (Date.now() < (this.suppressCarouselClickUntil || 0)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
    }

    bindDesktopGestures() {
      const surface = this.elements.viewer.querySelector('.product__media-list');
      if (!surface) return;
      const enabled = () => this.mql.matches && this.dataset.desktopLayout !== 'stacked';
      let drag = null;
      let suppressClickUntil = 0;
      let wheelTotal = 0;
      let wheelLast = 0;
      let wheelUsed = false;
      const reset = () => {
        if (!drag) return;
        const finished = drag;
        drag = null;
        finished.media?.style.removeProperty('transform');
        surface.classList.remove('canvoil-gallery-dragging');
        if (surface.hasPointerCapture(finished.id)) surface.releasePointerCapture(finished.id);
      };
      surface.addEventListener('pointerenter', () => {
        if (enabled()) this.prepareNextImage();
      });
      surface.addEventListener('dragstart', (event) => {
        if (enabled()) event.preventDefault();
      });
      surface.addEventListener('pointerdown', (event) => {
        if (!enabled() || event.pointerType !== 'mouse' || event.button !== 0) return;
        const item = event.target.closest('.product__media-item');
        const media = item?.querySelector('.product__media');
        if (!media || !media.querySelector('img')) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, moved: false, media };
      });
      surface.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        if (!enabled()) return reset();
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) return reset();
          if (Math.abs(dx) < 8) return;
          drag.moved = true;
          surface.setPointerCapture(event.pointerId);
          surface.classList.add('canvoil-gallery-dragging');
        }
        event.preventDefault();
        drag.dx = dx;
        drag.media.style.transform = `translateX(${Math.max(-72, Math.min(72, dx * 0.4))}px)`;
      });
      surface.addEventListener('pointerup', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const { dx, moved } = drag;
        if (moved) suppressClickUntil = Date.now() + 400;
        reset();
        if (moved && Math.abs(dx) >= 40 && enabled()) this.navigateMedia(dx < 0 ? 1 : -1);
      });
      surface.addEventListener('pointercancel', reset);
      surface.addEventListener('lostpointercapture', () => {
        if (drag) {
          suppressClickUntil = Date.now() + 400;
          reset();
        }
      });
      surface.addEventListener('pointerleave', () => { if (drag && !drag.moved) reset(); });
      surface.addEventListener('click', (event) => {
        if (Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      surface.addEventListener('wheel', (event) => {
        if (!enabled() || event.ctrlKey) return;
        let dx = event.deltaX;
        if (event.shiftKey && !dx) dx = event.deltaY;
        if (!dx || (!event.shiftKey && Math.abs(dx) <= Math.abs(event.deltaY))) return;
        event.preventDefault();
        const now = Date.now();
        if (now - wheelLast > 180) { wheelTotal = 0; wheelUsed = false; }
        wheelLast = now;
        if (wheelUsed) return;
        wheelTotal += dx * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? surface.clientWidth : 1);
        if (Math.abs(wheelTotal) >= 45) {
          wheelUsed = true;
          this.navigateMedia(wheelTotal > 0 ? 1 : -1);
        }
      }, { passive: false });
    }

    prepareNextImage() {
      if (!this.mql.matches || this.dataset.desktopLayout === 'stacked' || navigator.connection?.saveData) return;
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const current = thumbnails.findIndex(item => item.querySelector('[aria-current="true"]'));
      const next = thumbnails[current + 1];
      const media = next && this.elements.viewer.querySelector(`[data-media-id="${next.dataset.target}"]`);
      const image = media?.querySelector('.product__media img');
      if (image) this.prepareImage(image);
    }

    navigateMedia(direction) {
      if (this.carousel) return this.navigateCarousel(direction);
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      if (thumbnails.length < 2) return;
      const current = thumbnails.findIndex(item => item.querySelector('[aria-current="true"]'));
      const next = ((current < 0 ? 0 : current) + direction + thumbnails.length) % thumbnails.length;
      this.setActiveMedia(thumbnails[next].dataset.target, false);
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

  async setActiveMedia(mediaId, prepend, options = {}) {
    if (this.carousel) return this.selectCarouselMedia(mediaId, { ...options, external: prepend && options.source !== 'lightbox' });
    const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
    const request = this.mediaRequest = (this.mediaRequest || 0) + 1;
    this.elements.viewer.removeAttribute('aria-busy');
    if (activeMedia) {
      // Keep the current desktop image visible until the requested image is ready.
      if (!prepend && this.mql.matches && this.dataset.desktopLayout !== 'stacked') {
        if (activeMedia.classList.contains('is-active')) return;
        const image = activeMedia.querySelector('.product__media img');
        if (image) {
          this.elements.viewer.setAttribute('aria-busy', 'true');
          const ready = await this.prepareImage(image);
          if (request !== this.mediaRequest) return;
          this.elements.viewer.removeAttribute('aria-busy');
          if (!ready) return;
        }
      }
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
      if (request !== this.mediaRequest) return;
      if (this.elements.thumbnails) {
        activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
      }
      if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
        activeMedia.scrollIntoView({behavior: 'smooth'});
      }
    });
    this.playActiveMedia(activeMedia);

    if (!this.elements.thumbnails) return;
    const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
    this.setActiveThumbnail(activeThumbnail);
    if (!prepend) this.prepareNextImage();
    //       this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
  }
}

    prepareImage(image) {
      // Match the actual gallery column instead of requesting near-full-page images.
      const width = Math.ceil(this.elements.viewer.clientWidth);
      if (this.mql.matches && width > 0) image.sizes = `${width}px`;
      this.imagePreparation ||= new WeakMap();
      this.decodedSources ||= new WeakMap();
      if (image.complete && image.naturalWidth > 0 && image.currentSrc &&
          this.decodedSources.get(image) === image.currentSrc) return Promise.resolve(true);
      if (this.imagePreparation.has(image)) return this.imagePreparation.get(image);
      const preparation = new Promise((resolve) => {
        let settled = false;
        const finish = (ready) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          resolve(ready);
        };
        const failed = () => finish(false);
        const loaded = async () => {
          if (!image.naturalWidth) return finish(false);
          try {
            if (image.decode) await image.decode();
            finish(image.naturalWidth > 0);
          } catch (_) {
            finish(false);
          }
        };
        const timeout = setTimeout(failed, 15000);
        image.addEventListener('load', loaded, { once: true });
        image.addEventListener('error', failed, { once: true });
        // Prepare only the image targeted by pointer, keyboard focus or click.
        image.loading = 'eager';
        if (image.complete) loaded();
      });
      this.imagePreparation.set(image, preparation);
      preparation.then((ready) => {
        this.imagePreparation.delete(image);
        if (ready) this.decodedSources.set(image, image.currentSrc);
      });
      return preparation;
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
      if (this.counter) {
        const position = this.mediaOrder.indexOf(thumbnail.dataset.target) + 1;
        if (position > 0) this.counter.textContent = String(position).padStart(2, '0');
      }

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
